import { CircleCheck, Film, IdCard, MapPin } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import React, { FC } from 'react';

import { CollaboratorInfo } from '~components/uas-collaborators/CollaboratorInfoForm';
import { Taker } from '~components/uas/Success';

interface Props {
  data: Taker & CollaboratorInfo;
}

const frequencies = {
  DAILY: 'Diario',
  WEEKLY: 'A la semana',
  MONTHLY: 'Al Mes',
  BIWEEKLY: 'A la quincena',
};

const Success: FC<Props> = ({ data }) => {
  return (
    <div className="flex flex-col items-center gap-6 px-6 py-8 md:px-12">
      <span className="rounded-full bg-success/5 p-4 text-success dark:bg-success/20">
        <CircleCheck />
      </span>
      <div>
        <h1 className="h3 text-center font-bold">¡Registro Exitoso!</h1>
        <p className="text-support">Bienvenido al programa de beneficios Citicinemas x UAS</p>
      </div>
      <div className="flex w-full flex-col gap-2 py-8">
        <p className="text-support text-xs font-bold uppercase">Información del registro</p>
        <p>
          <span className="text-support">Nombre:</span> {data.name} {data.lastName}
        </p>
        <p>
          <span className="text-support">Teléfono:</span> {data.phone}
        </p>
        <p>
          <span className="text-support">Número de empleado:</span> {data.employeeNumber}
        </p>
      </div>
      <div className="flex w-full flex-col items-center gap-4 rounded-2xl border-2 border-info/50 bg-accent p-4">
        <span className="rounded-full bg-info/10 p-4 text-info">
          <IdCard />
        </span>
        <span className="text-support text-xs font-bold uppercase">Tu número de empleado</span>
        <p className="text-center text-2xl font-bold text-info">
          {data.employeeNumber || data.externalId.split('-').pop()}
        </p>
        <p className="text-support">Usa este número en los kioskos de Citicinemas</p>
      </div>
      <div className="w-full rounded-lg border bg-card p-4 dark:border-neutral-600">
        <h3 className="mb-3 flex items-center gap-2 font-semibold text-foreground">
          <MapPin className="h-5 w-5 text-primary" />
          ¿Cómo usar tus beneficios?
        </h3>
        <ol className="text-support list-inside list-decimal space-y-2 text-sm">
          <li>Visita cualquier Citicinemas participante</li>
          <li>Busca el kiosko de autoservicio</li>
          <li>De la lista de afiliados selecciona la Universidad Autonoma de Sinaloa</li>
          <li>
            Ingresa tu número de empleado:{' '}
            <span className="font-semibold text-primary">{data.employeeNumber}</span>
          </li>
          <li>Ingresa el código que enviamos por SMS a tu celular.</li>
          <li>Selecciona e imprime tus cupones de descuento</li>
          <li>Presenta el cupón en taquilla o dulcería</li>
        </ol>
      </div>
      <div className="w-full">
        <p className="text-support mb-6 text-xs font-bold uppercase">Tus Beneficios Disponibles</p>
        <ul className="flex flex-col divide-y rounded-lg border dark:divide-neutral-600 dark:border-neutral-600">
          {data.availableBenefits.map((benefit) => (
            <li key={benefit.id} className="flex items-center gap-3 p-4">
              <span className="h-12 w-12 overflow-hidden rounded-full">
                {benefit?.icon?.url && (
                  <Image src={benefit.icon.url} width={48} height={48} alt="" />
                )}
              </span>
              <div className="flex-1">
                <h4 className="font-semibold">{benefit.title}</h4>
                <p className="text-support mb-0 text-sm">{benefit.description}</p>
              </div>
              <div className="flex flex-col items-center gap-1 border-l pl-4 text-center dark:border-l-neutral-600">
                <span>{benefit.quantity}</span>
                <span className="text-support text-xs font-bold uppercase">
                  {frequencies[benefit.frequency as keyof typeof frequencies]}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="grid w-full grid-cols-2 gap-6">
        <Link className="btn btn-primary" href="/complejos">
          <MapPin size={16} /> <span>Ubicar Citicinemas</span>
        </Link>
        <Link className="btn dark:btn-dark" href="/">
          <Film size={16} /> <span>Ver Cartelera</span>
        </Link>
      </div>
    </div>
  );
};

export default Success;
